import React from 'react';
import { RefreshCw } from 'lucide-react';
import { formatRelativeThai } from '../utils/helpers';

export interface RealtimeSyncStatus {
  /** polling: reading the sites' update feeds; checking: full check of every manga */
  phase: 'idle' | 'polling' | 'checking';
  lastSyncAt: number | null;
  progress: { current: number; total: number } | null;
}

interface RealtimeStatusProps {
  status: RealtimeSyncStatus;
  onSyncNow: () => void;
}

/**
 * Live indicator for the automatic chapter tracking. Tapping it forces a full check right away,
 * but nothing depends on that: updates arrive on their own.
 */
export const RealtimeStatus: React.FC<RealtimeStatusProps> = ({ status, onSyncNow }) => {
  const busy = status.phase !== 'idle';
  let label: string;
  if (status.phase === 'checking' && status.progress) {
    label = `กำลังเช็ค ${status.progress.current}/${status.progress.total}`;
  } else if (busy) {
    label = 'กำลังอัปเดต...';
  } else if (status.lastSyncAt) {
    label = `อัปเดต${formatRelativeThai(status.lastSyncAt)}`;
  } else {
    label = 'เรียลไทม์';
  }

  return (
    <button
      type="button"
      onClick={onSyncNow}
      disabled={busy}
      title={`ติดตามตอนใหม่อัตโนมัติแบบเรียลไทม์ · ${label} (แตะเพื่อเช็คทุกเรื่องทันที)`}
      className="h-9 min-w-[36px] px-2.5 rounded-lg flex items-center justify-center gap-2 text-xs font-medium text-zinc-400 hover:text-zinc-100 hover:bg-white/[0.06] transition-colors whitespace-nowrap shrink-0 disabled:cursor-default"
    >
      {busy ? (
        <RefreshCw className="w-3.5 h-3.5 text-emerald-400 animate-spin" />
      ) : (
        <span className="relative flex w-2 h-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-50 animate-ping" />
          <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-400" />
        </span>
      )}
      <span className="hidden xl:inline tabular-nums">{label}</span>
    </button>
  );
};
